import React, { useEffect } from 'react';
import { Link } from 'react-router-dom';
import { ChevronLeft, FileText, Mail } from 'lucide-react';
import Navbar from '../components/Navbar';
import Footer from '../components/Footer';
import SEOHead from '../components/SEOHead';
import { SEO_IMAGES } from '../utils/seo';

/**
 * TermsPage — Termos e Condições de Uso do BuscaDoutor (Versão 1.0 — Maio de 2026).
 * Conteúdo fornecido pela equipe jurídica e estruturado em seções legais.
 */
export default function TermsPage() {
  useEffect(() => {
    window.scrollTo(0, 0);
    document.title = 'Termos e Condições de Uso | BuscaDoutor';
  }, []);

  const sections = [
    {
      id: 'identificacao',
      title: '1. Identificação e Escopo',
      paragraphs: [
        'Estes Termos e Condições de Uso ("Termos") regulam o acesso e a utilização da plataforma BuscaDoutor, operada por [RAZÃO SOCIAL DA EMPRESA], pessoa jurídica de direito privado, inscrita no CNPJ sob o nº [CNPJ], com sede em [ENDEREÇO COMPLETO] (doravante "BuscaDoutor" ou "Nós").',
        'O BuscaDoutor é uma plataforma digital de busca e divulgação de informações públicas sobre profissionais de saúde. Nossa função é exclusivamente conectar pacientes e usuários a informações disponíveis publicamente sobre médicos e outros profissionais de saúde cadastrados em nossa base de dados.',
        'Ao acessar ou utilizar o BuscaDoutor, você ("Usuário" ou "Paciente") declara que leu, compreendeu e concorda integralmente com estes Termos. Caso não concorde, por favor, não utilize nossa plataforma.',
      ],
    },
    {
      id: 'definicoes',
      title: '2. Definições',
      intro: 'Para fins destes Termos, as seguintes expressões têm o significado abaixo:',
      list: [
        <><strong>"Plataforma":</strong> o site e/ou aplicativo BuscaDoutor, disponível em www.buscadoutor.com.br, e seus sistemas associados.</>,
        <><strong>"Usuário" ou "Paciente":</strong> qualquer pessoa física que acesse a Plataforma para buscar informações sobre profissionais de saúde.</>,
        <><strong>"Profissional":</strong> médico, dentista ou qualquer outro profissional de saúde cujas informações públicas estejam disponíveis na Plataforma.</>,
        <><strong>"Informações Públicas":</strong> dados disponíveis em fontes abertas, como nome, especialidade, endereço de atendimento, telefone de contato e avaliações de usuários.</>,
      ],
    },
    {
      id: 'natureza',
      title: '3. Natureza da Plataforma e Limitações',
      intro: 'O BuscaDoutor é um mecanismo de busca e diretório de profissionais de saúde. É fundamental que o Usuário compreenda:',
      list: [
        'O BuscaDoutor NÃO é uma plataforma de agendamento de consultas, NÃO realiza triagem médica, NÃO oferece serviços de telemedicina e NÃO presta qualquer serviço de saúde.',
        'O BuscaDoutor NÃO possui vínculo empregatício, contratual ou de qualquer natureza com os Profissionais listados, exceto nos casos em que o Profissional tenha aderido voluntariamente a algum serviço específico oferecido pela Plataforma.',
        'As informações exibidas na Plataforma são coletadas de fontes públicas e/ou fornecidas pelos próprios Profissionais, cabendo ao Usuário verificar sua atualidade e precisão diretamente com o Profissional antes de qualquer decisão.',
        'A relação estabelecida a partir de um contato entre Usuário e Profissional é exclusivamente entre as duas partes, sem qualquer intermediação, responsabilidade ou garantia por parte do BuscaDoutor.',
      ],
    },
    {
      id: 'cadastro',
      title: '4. Cadastro e Perfil de Profissionais',
      subsections: [
        {
          subtitle: '4.1 Origem das Informações',
          paragraphs: ['As informações sobre Profissionais exibidas na Plataforma podem ter duas origens:'],
          list: [
            <><strong>Fontes públicas:</strong> informações coletadas de cadastros públicos, conselhos profissionais, sites oficiais e outros meios de acesso público.</>,
            <><strong>Cadastro voluntário:</strong> informações fornecidas diretamente pelo próprio Profissional, que ao fazê-lo declara que são verdadeiras, precisas e atualizadas.</>,
          ],
        },
        {
          subtitle: '4.2 Responsabilidade pelas Informações',
          paragraphs: [
            'O BuscaDoutor envidará seus melhores esforços para manter as informações atualizadas, porém não se responsabiliza por eventuais imprecisões, desatualizações ou erros nas informações exibidas, sejam elas provenientes de fontes públicas ou fornecidas pelos Profissionais.',
            'Profissionais que identifiquem informações incorretas sobre si mesmos podem solicitar a correção ou remoção de seus dados através do canal indicado na Seção 10 (Contato).',
          ],
        },
      ],
    },
    {
      id: 'avaliacoes',
      title: '5. Avaliações e Opiniões de Usuários',
      intro: 'A Plataforma pode permitir que Usuários registrem avaliações e opiniões sobre os Profissionais. A esse respeito:',
      list: [
        'As avaliações refletem a opinião pessoal de cada Usuário e não representam a posição do BuscaDoutor.',
        'É terminantemente proibida a publicação de avaliações falsas, enganosas, ofensivas, discriminatórias ou que violem direitos de terceiros.',
        'O BuscaDoutor se reserva o direito de remover avaliações que violem estes Termos ou a legislação aplicável, sem necessidade de aviso prévio.',
        'O Usuário é o único responsável pelo conteúdo das avaliações que publicar e concorda em indenizar o BuscaDoutor por quaisquer danos decorrentes do descumprimento desta Seção.',
      ],
    },
    {
      id: 'obrigacoes',
      title: '6. Obrigações e Responsabilidades do Usuário',
      intro: 'Ao utilizar a Plataforma, o Usuário compromete-se a:',
      list: [
        'Utilizar a Plataforma exclusivamente para fins lícitos e de acordo com estes Termos.',
        'Não utilizar a Plataforma para coletar, armazenar ou processar dados pessoais de Profissionais para fins comerciais, de marketing ou quaisquer outros fins não autorizados.',
        'Não realizar scraping, crawling ou qualquer forma de extração automatizada de dados da Plataforma sem autorização expressa e prévia do BuscaDoutor.',
        'Não tentar acessar áreas restritas da Plataforma, interferir em seu funcionamento ou comprometer sua segurança.',
        'Não publicar conteúdo falso, ofensivo, discriminatório ou que viole direitos de terceiros.',
        'Verificar, sempre por meios próprios, a atualidade e veracidade das informações antes de tomar qualquer decisão com base nos dados exibidos na Plataforma.',
      ],
    },
    {
      id: 'limitacao',
      title: '7. Limitação de Responsabilidade',
      intro: 'O BuscaDoutor atua exclusivamente como um mecanismo de busca e diretório de informações públicas. Em razão dessa natureza, o BuscaDoutor:',
      list: [
        'Não se responsabiliza pela qualidade, resultado ou adequação dos serviços prestados pelos Profissionais listados na Plataforma.',
        'Não se responsabiliza por erros, imprecisões ou desatualizações nas informações exibidas, seja qual for a origem dessas informações.',
        'Não garante a disponibilidade ininterrupta da Plataforma, podendo esta ser suspensa temporariamente para manutenção, atualização ou por motivos de força maior.',
        'Não se responsabiliza por danos diretos, indiretos, incidentais ou consequentes decorrentes do uso ou da impossibilidade de uso da Plataforma.',
        'Não se responsabiliza pela conduta ou pelas ações dos Profissionais listados, nem pelo resultado de qualquer consulta, tratamento ou procedimento realizado por eles.',
      ],
      outro: 'O Usuário reconhece que a decisão de buscar atendimento com qualquer Profissional listado na Plataforma é de sua exclusiva responsabilidade, devendo sempre realizar sua própria avaliação antes de qualquer decisão de saúde.',
    },
    {
      id: 'propriedade',
      title: '8. Propriedade Intelectual',
      paragraphs: [
        'Todos os elementos da Plataforma, incluindo, mas não se limitando a, sua estrutura, design, código-fonte, logotipo, marcas, textos e funcionalidades, são de propriedade exclusiva do BuscaDoutor ou de seus licenciantes, e estão protegidos pela legislação brasileira de direitos autorais e propriedade intelectual.',
        'É vedada qualquer reprodução, distribuição, modificação, criação de obras derivadas ou uso comercial sem autorização prévia e expressa do BuscaDoutor.',
        'O acesso à Plataforma não confere ao Usuário qualquer direito sobre a propriedade intelectual aqui descrita.',
      ],
    },
    {
      id: 'dados',
      title: '9. Dados Pessoais e Privacidade',
      paragraphs: [
        'O BuscaDoutor trata os dados pessoais dos Usuários em conformidade com a Lei Geral de Proteção de Dados (Lei nº 13.709/2018 — LGPD) e demais normas aplicáveis.',
        'As práticas de coleta, uso, armazenamento e compartilhamento de dados pessoais estão detalhadas em nossa Política de Privacidade, disponível em www.buscadoutor.com.br/privacidade, que é parte integrante destes Termos.',
        'As informações sobre Profissionais exibidas na Plataforma são de natureza pública e profissional, coletadas de fontes abertas ou fornecidas pelos próprios Profissionais.',
        'Profissionais que desejem exercer seus direitos como titulares de dados (acesso, correção, exclusão, portabilidade, entre outros) podem fazê-lo através do canal indicado na Seção 10.',
      ],
    },
    {
      id: 'contato',
      title: '10. Contato e Suporte',
      intro: 'Para dúvidas, sugestões, solicitações de correção de informações, exercício de direitos de titulares de dados ou qualquer outra comunicação, o Usuário ou Profissional pode entrar em contato conosco pelos seguintes canais:',
      list: [
        <><strong>E-mail:</strong> contato@buscadoutor.com.br</>,
        <><strong>Formulário de contato:</strong> www.buscadoutor.com.br/contato</>,
        <><strong>Encarregado de Dados (DPO):</strong> dpo@buscadoutor.com.br</>,
      ],
      outro: 'Nos comprometemos a responder as solicitações no prazo máximo de 15 (quinze) dias úteis.',
    },
    {
      id: 'alteracoes',
      title: '11. Alterações nos Termos',
      paragraphs: [
        'O BuscaDoutor reserva-se o direito de atualizar estes Termos a qualquer momento, mediante publicação da versão atualizada na Plataforma.',
        'Alterações relevantes serão comunicadas com antecedência mínima de 10 (dez) dias, por meio de aviso na própria Plataforma ou por e-mail, quando o Usuário tiver fornecido este dado.',
        'O uso continuado da Plataforma após a entrada em vigor das alterações implica na aceitação dos novos Termos. Caso o Usuário não concorde com as alterações, deverá cessar o uso da Plataforma.',
      ],
    },
    {
      id: 'gerais',
      title: '12. Disposições Gerais',
      list: [
        'Estes Termos são regidos pelas leis da República Federativa do Brasil.',
        'Fica eleito o foro da comarca de [CIDADE/ESTADO] para dirimir quaisquer controvérsias oriundas destes Termos, com renúncia expressa a qualquer outro, por mais privilegiado que seja.',
        'A eventual invalidade de qualquer cláusula destes Termos não afetará a validade das demais.',
        'A omissão do BuscaDoutor em exigir o cumprimento de qualquer disposição destes Termos não constitui renúncia ao direito de exigi-lo no futuro.',
      ],
    },
  ];

  return (
    <div className="min-h-screen bg-white" data-testid="terms-page">
      <SEOHead
        title="Termos e Condições de Uso | BuscaDoutor"
        description="Consulte os termos e condições de uso da plataforma BuscaDoutor."
        canonicalUrl="/termos"
        imageUrl={SEO_IMAGES.security}
      />
      <Navbar />

      {/* Hero */}
      <section className="relative overflow-hidden bg-gradient-to-br from-[#F4FAF8] via-white to-[#F4FAF8] py-16 md:py-20">
        <div className="container-app">
          <Link
            to="/"
            className="inline-flex items-center gap-2 text-[#59706A] hover:text-primary mb-8 text-sm transition-colors"
            data-testid="terms-back-link"
          >
            <ChevronLeft className="w-4 h-4" />
            Voltar
          </Link>

          <div className="max-w-3xl">
            <div className="inline-flex items-center gap-2 px-4 py-1.5 bg-primary/10 rounded-full mb-6">
              <FileText className="w-3.5 h-3.5 text-primary" />
              <span className="text-xs font-bold uppercase tracking-[0.2em] text-primary">Documento legal</span>
            </div>

            <h1
              className="text-4xl sm:text-5xl lg:text-6xl font-bold text-[#1A2421] leading-[1.05] tracking-tight mb-4"
              style={{ fontFamily: 'Outfit, sans-serif' }}
            >
              Termos e Condições de <span className="text-primary">Uso</span>
            </h1>

            <p className="text-lg text-[#59706A] leading-relaxed">
              Encontre o profissional de saúde certo para você.
            </p>
            <p className="text-sm text-[#59706A]/70 mt-3">
              Versão 1.0 — Maio de 2026
            </p>
          </div>
        </div>
        <div className="absolute -top-24 -right-24 w-96 h-96 bg-primary/5 rounded-full blur-3xl pointer-events-none" />
      </section>

      {/* Content */}
      <section className="py-12 md:py-16">
        <div className="container-app grid lg:grid-cols-[260px_1fr] gap-10 lg:gap-16 items-start">
          {/* TOC sidebar */}
          <aside className="hidden lg:block sticky top-24" data-testid="terms-toc">
            <h3 className="text-xs font-bold uppercase tracking-[0.2em] text-primary mb-4">Sumário</h3>
            <ol className="space-y-2 text-sm">
              {sections.map((s) => (
                <li key={s.id}>
                  <a
                    href={`#${s.id}`}
                    className="text-[#59706A] hover:text-primary transition-colors leading-snug block"
                  >
                    {s.title}
                  </a>
                </li>
              ))}
            </ol>
          </aside>

          {/* Sections */}
          <article className="prose prose-slate max-w-none" data-testid="terms-content">
            {sections.map((s) => (
              <section key={s.id} id={s.id} className="mb-12 scroll-mt-24">
                <h2
                  className="text-2xl md:text-3xl font-semibold text-[#1A2421] mb-5 leading-tight"
                  style={{ fontFamily: 'Outfit, sans-serif' }}
                >
                  {s.title}
                </h2>

                {s.paragraphs?.map((p, i) => (
                  <p key={i} className="text-[#3F4F4B] leading-relaxed mb-4">{p}</p>
                ))}

                {s.intro && <p className="text-[#3F4F4B] leading-relaxed mb-4">{s.intro}</p>}

                {s.list && (
                  <ul className="space-y-3 mb-4 list-disc pl-5 marker:text-primary">
                    {s.list.map((item, i) => (
                      <li key={i} className="text-[#3F4F4B] leading-relaxed pl-1">{item}</li>
                    ))}
                  </ul>
                )}

                {s.outro && <p className="text-[#3F4F4B] leading-relaxed mt-4">{s.outro}</p>}

                {s.subsections?.map((sub, i) => (
                  <div key={i} className="mt-6">
                    <h3
                      className="text-lg font-semibold text-[#1A2421] mb-3"
                      style={{ fontFamily: 'Outfit, sans-serif' }}
                    >
                      {sub.subtitle}
                    </h3>
                    {sub.paragraphs?.map((p, j) => (
                      <p key={j} className="text-[#3F4F4B] leading-relaxed mb-3">{p}</p>
                    ))}
                    {sub.list && (
                      <ul className="space-y-3 list-disc pl-5 marker:text-primary">
                        {sub.list.map((item, j) => (
                          <li key={j} className="text-[#3F4F4B] leading-relaxed pl-1">{item}</li>
                        ))}
                      </ul>
                    )}
                  </div>
                ))}
              </section>
            ))}

            {/* Footer note */}
            <div className="mt-12 pt-8 border-t border-border/40 text-sm text-[#59706A]">
              <p className="mb-3">
                <strong className="text-[#1A2421]">BuscaDoutor</strong> — Versão 1.0 — Maio de 2026
              </p>
              <p>
                <a
                  href="https://www.buscadoutor.com.br"
                  className="text-primary hover:underline"
                >
                  www.buscadoutor.com.br
                </a>
                {' | '}
                <a
                  href="mailto:contato@buscadoutor.com.br"
                  className="text-primary hover:underline inline-flex items-center gap-1"
                >
                  <Mail className="w-3.5 h-3.5" />
                  contato@buscadoutor.com.br
                </a>
              </p>
            </div>
          </article>
        </div>
      </section>

      <Footer />
    </div>
  );
}
